import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useCustomToast } from 'design-system';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'api/v1/cms/exclusive-deals/categories/tree';
export const getDealCategoryTreeEndpointIdentifier = endpoint;

export interface DealCategoryTreeNode {
  id: number;
  title: string;
  depth: number;
  children: DealCategoryTreeNode[];
}

interface ResponseData {
  data: DealCategoryTreeNode[];
}

type TData = ResponseData;
type TError = AxiosError;
type TQueryKey = [typeof endpoint];
type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

/**
 * Full catalog category tree for the exclusive-deals grid category picker.
 * BE caches it ~1 week, so a long client staleTime is safe and avoids refetching
 * across the many category cells + the picker modal (React Query dedupes the request).
 */
export function useGetDealCategoryTreeApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const isLoggedIn = useIsLoggedIn();

  const queryFn = async (): Promise<TData> => {
    try {
      const { data } = await http.get(endpoint);
      return data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;
      toast({ description: error.response.data.message, status: 'error' });
      throw error;
    }
  };

  return useQueryWrapper([endpoint], queryFn, {
    enabled: isLoggedIn,
    staleTime: 60 * 60 * 1000, // 1h client cache (BE caches a week)
    ...options,
  });
}
